import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import {
  fetchCourseReviews,
  submitCourseReview,
  type CourseReviewSummary,
} from "@/services/api/courses";

type Review = {
  id: string;
  authorName: string;
  rating: number;
  body: string | null;
  createdAt: string;
};

type Props = {
  courseId: string;
  initialReviews: Review[];
  initialSummary: CourseReviewSummary;
  canSubmitReview: boolean;
};

export function CourseReviewsPanel({
  courseId,
  initialReviews,
  initialSummary,
  canSubmitReview,
}: Props) {
  const t = useTranslations("reviews");
  const tRoot = useTranslations();
  const [reviews, setReviews] = useState(initialReviews);
  const [summary, setSummary] = useState(initialSummary);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submitCourseReview(courseId, { rating, body: body.trim() || undefined });
      const data = await fetchCourseReviews(courseId);
      setReviews(data.reviews);
      setSummary(data.summary);
      setBody("");
    } catch (err) {
      // SPA-5 (C4): the API's code, translated — never its English message.
      setError(apiErrorText(err, tRoot, "reviews.submitFailed", { namespace: "reviews.errors" }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="mt-8 rounded-lg border border-[var(--border)] bg-white p-6"
      data-testid="course-reviews"
    >
      <h2 className="text-lg font-medium">{t("title")}</h2>
      <p className="mt-2 text-sm text-[var(--text-muted)]" data-testid="course-reviews-summary">
        {summary.count > 0
          ? t("summary", { average: summary.averageRating, count: summary.count })
          : t("noReviewsYet")}
      </p>

      <ul className="mt-4 space-y-3">
        {reviews.map((review) => (
          <li
            key={review.id}
            className="rounded border border-[var(--border)] p-3 text-sm"
            data-testid={`course-review-${review.id}`}
          >
            <p className="font-medium">
              {review.authorName} · {t("stars", { rating: review.rating })}
            </p>
            {review.body ? (
              <p className="mt-1 text-[var(--text-secondary)]">{review.body}</p>
            ) : null}
          </li>
        ))}
      </ul>

      {canSubmitReview ? (
        <form className="mt-6 space-y-3" onSubmit={(event) => void handleSubmit(event)}>
          <h3 className="text-sm font-medium">{t("writeReview")}</h3>
          <label className="block text-sm">
            {t("ratingLabel")}
            <select
              className="mt-1 rounded border border-[var(--border-strong)] bg-[var(--surface-1)] p-2 text-[var(--text-primary)]"
              value={rating}
              onChange={(event) => setRating(Number(event.target.value))}
              data-testid="course-review-rating"
            >
              {[5, 4, 3, 2, 1].map((value) => (
                <option key={value} value={value}>
                  {t("stars", { rating: value })}
                </option>
              ))}
            </select>
          </label>
          <textarea
            className="w-full rounded border border-[var(--border-strong)] bg-[var(--surface-1)] p-2 text-sm text-[var(--text-primary)]"
            rows={3}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={t("bodyPlaceholder")}
            data-testid="course-review-body"
          />
          {error ? (
            <p className="text-sm text-red-600" role="alert" data-testid="course-review-error">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-[var(--ink-9)] px-3 py-1.5 text-sm text-white disabled:opacity-50"
            data-testid="course-review-submit"
          >
            {submitting ? t("working") : t("submitReview")}
          </button>
        </form>
      ) : null}
    </section>
  );
}
